// One section of the prep editor: a grip, a heading you type in, a trash icon,
// and a body you type in. The body is a contentEditable whose html is set from
// outside only when it differs from what is already there, so a poll that
// brings back the text just saved never moves the caret.

import { useCallback, useEffect, useLayoutEffect, useRef, useState, type JSX, type PointerEvent as ReactPointerEvent } from "react";
import { handleKey, labelTables, pastePlain } from "../lib/prepDom";
import { sanitizeHtml, toEditable, type Sec } from "../lib/prepEdit";
import { ChevronIcon, GripIcon, TrashIcon } from "./icons";

interface PrepSectionProps {
  readonly sec: Sec;
  /** The section is lifted: its place stays as an empty gap. */
  readonly lifted: boolean;
  readonly setSection: (id: string, el: HTMLElement | null) => void;
  readonly setBody: (id: string, el: HTMLElement | null) => void;
  readonly setTitle: (id: string, el: HTMLTextAreaElement | null) => void;
  readonly onHtml: (id: string) => void;
  readonly onTitle: (id: string) => void;
  readonly onBlur: (id: string) => void;
  readonly onDelete: (id: string) => void;
  /** `hold` in ms; omitted means the usual press-and-hold. */
  readonly onHold: (e: ReactPointerEvent, id: string, hold?: number) => void;
}

export function PrepSection({ sec, lifted, setSection, setBody, setTitle, onHtml, onTitle, onBlur, onDelete, onHold }: PrepSectionProps): JSX.Element {
  const bodyRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLTextAreaElement>(null);
  // A section the server marks `fold` starts closed in each view; its heading
  // is the toggle. Per view only: nothing here is saved.
  const [open, setOpen] = useState(!sec.fold);

  const focusBody = useCallback((): void => {
    const body = bodyRef.current;
    if (!body) return;
    body.focus();
    const r = document.createRange();
    r.selectNodeContents(body);
    r.collapse(true);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(r);
  }, []);

  // The body takes html from outside only when it differs from what is there.
  // A focused body takes it too when nothing was typed since it was last
  // painted, with the caret kept where it was: a line added elsewhere while
  // his cursor sat in Questions never showed (2026-10-19, the Chris agenda).
  const paintedRef = useRef<string | null>(null);
  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    const now = sanitizeHtml(el.innerHTML);
    if (el.innerHTML !== "" && now === sec.html) {
      paintedRef.current = sec.html;
      return;
    }
    if (el.innerHTML !== "" && document.activeElement === el) {
      // Typed in since the last paint: the merge keeps it, and this is stale.
      if (paintedRef.current === null || now !== paintedRef.current) return;
      const at = caretOffset(el);
      el.innerHTML = toEditable(sec.html);
      labelTables(el);
      paintedRef.current = sec.html;
      if (at !== null) placeCaret(el, at);
      return;
    }
    el.innerHTML = toEditable(sec.html);
    labelTables(el);
    paintedRef.current = sec.html;
  }, [sec.html]);

  useLayoutEffect(() => {
    const el = titleRef.current;
    if (el && el.value !== sec.title && document.activeElement !== el) el.value = sec.title;
    if (el) fitTitle(el);
  }, [sec.title]);

  // A long heading wraps to as many lines as it needs, and refits when the
  // column it sits in changes width.
  useEffect(() => {
    const el = titleRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    let width = el.clientWidth;
    const ro = new ResizeObserver(() => {
      if (el.clientWidth === width) return;
      width = el.clientWidth;
      fitTitle(el);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <section
      className={lifted ? "pe__sec pe__sec--lifted" : "pe__sec"}
      data-sec={sec.id}
      ref={(el) => setSection(sec.id, el)}
    >
      <div
        className="pe__head"
        onPointerDown={(e) => {
          const target = e.target as HTMLElement;
          if (target.closest("button")) return;
          if (target.closest(".pe__grip")) e.preventDefault();
          onHold(e, sec.id, target.closest(".pe__grip") ? 0 : undefined);
        }}
      >
        <span className="pe__grip" aria-hidden="true">
          <GripIcon size={18} />
        </span>
        {sec.fold ? (
          <button
            type="button"
            className="pe__fold"
            aria-expanded={open}
            aria-label={open ? `Close ${sec.title}` : `Open ${sec.title}`}
            onClick={() => setOpen(!open)}
          >
            <ChevronIcon size={18} />
          </button>
        ) : null}
        <textarea
          ref={(el) => {
            titleRef.current = el;
            setTitle(sec.id, el);
          }}
          className="pe__title"
          rows={1}
          maxLength={200}
          defaultValue={sec.title}
          aria-label="Section heading"
          enterKeyHint="next"
          onInput={(e) => {
            const el = e.currentTarget;
            if (/[\r\n]/.test(el.value)) el.value = el.value.replace(/\s*[\r\n]+\s*/g, " ");
            fitTitle(el);
            onTitle(sec.id);
          }}
          onClick={() => {
            if (!open) setOpen(true);
          }}
          onBlur={(e) => {
            if (e.currentTarget.value.trim() === "") e.currentTarget.value = sec.title;
            onBlur(sec.id);
          }}
          onKeyDown={(e) => {
            if (e.key !== "Enter") return;
            e.preventDefault();
            if (!open) {
              setOpen(true);
              requestAnimationFrame(focusBody);
              return;
            }
            focusBody();
          }}
        />
        <button type="button" className="pe__trash" aria-label={`Delete ${sec.title}`} onClick={() => onDelete(sec.id)}>
          <TrashIcon size={18} />
        </button>
      </div>
      <div
        ref={(el) => {
          bodyRef.current = el;
          setBody(sec.id, el);
        }}
        className="pe__body"
        hidden={!open}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label={`${sec.title} notes`}
        onInput={(e) => {
          labelTables(e.currentTarget);
          onHtml(sec.id);
        }}
        onBlur={() => onBlur(sec.id)}
        onKeyDown={(e) => {
          handleKey(e.nativeEvent, e.currentTarget);
        }}
        onPaste={(e) => {
          e.preventDefault();
          pastePlain(e.currentTarget, e.clipboardData.getData("text/plain"));
        }}
        onDrop={(e) => {
          if (e.dataTransfer.files.length > 0) e.preventDefault();
        }}
      />
    </section>
  );
}

/** Grow a heading to its wrapped height; one line when it fits. */
function fitTitle(el: HTMLTextAreaElement): void {
  el.style.height = "auto";
  el.style.height = `${el.scrollHeight}px`;
}


/** The caret's place in a body, counted in characters from its start. */
function caretOffset(el: HTMLElement): number | null {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  const r = sel.getRangeAt(0);
  if (!el.contains(r.startContainer)) return null;
  const pre = document.createRange();
  pre.selectNodeContents(el);
  pre.setEnd(r.startContainer, r.startOffset);
  return pre.toString().length;
}

/** Puts the caret back `at` characters into a body (the end when it is shorter). */
function placeCaret(el: HTMLElement, at: number): void {
  const walk = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  let left = at;
  let node = walk.nextNode();
  const r = document.createRange();
  while (node) {
    const len = node.textContent?.length ?? 0;
    if (left <= len) {
      r.setStart(node, left);
      r.collapse(true);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(r);
      return;
    }
    left -= len;
    node = walk.nextNode();
  }
  r.selectNodeContents(el);
  r.collapse(false);
  const sel = window.getSelection();
  sel?.removeAllRanges();
  sel?.addRange(r);
}
